import { Dialog } from '@angular/cdk/dialog';
import { OverlayContainer } from '@angular/cdk/overlay';
import { NgComponentOutlet } from '@angular/common';
import { Component, computed, contentChild, Directive, effect, inject, input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { RouterOutlet } from '@angular/router';
import { LayoutService } from '@processpuzzle/util';
import { APPLICATION_CONTEXT, SidenavAutosizeDirective, SidenavContentOutletComponent, ThemeService } from '@processpuzzle/widgets';
import { AppDefinition } from '../../domain/app-definition';
import { AppRegionRenderer, RegionView } from './app-region.renderer';
import { layoutOf, themeDefaultsOf, themeVarsOf } from './app-shell.model';
import { NavOrientation } from './region-nav.component';
import { ShellApplicationContext } from './shell-application-context';
import { ShellOverlayContainer } from './shell-overlay-container';

/**
 * Marks content a host projects into the end of the shell's top row: `<div ppShellActions>…</div>`.
 *
 * For controls that belong to the platform rather than to the application — signing out, switching
 * organization, opening the designer. They come from the host and not from a header region, because a
 * definition decides what its regions hold, and a definition that could drop the designer button would
 * let a designer lock themselves out of the designer.
 *
 * **It brings its own overlay container.** Projected content resolves its dependencies through the
 * injectors of the template that declared it — the host's — not through the shell's, so the shell's
 * {@link ShellOverlayContainer} is out of its reach: a menu opened from one of these controls would render
 * under `<body>` and wear the host's theme. This element is inside the shell's host, which is all a
 * container needs to wear the application's.
 */
@Directive({
  selector: '[ppShellActions]',
  standalone: true,
  host: { class: 'pp-app-shell__actions' },
  providers: [{ provide: OverlayContainer, useClass: ShellOverlayContainer }],
})
export class ShellActionsDirective {}

/**
 * The run-time shell of a metadata-defined application: the chrome an `AppDefinition` describes, with a
 * `<router-outlet>` where its routes render.
 *
 * **This is the application, not a picture of one.** `AppPreviewComponent` hosts it with the draft so
 * that the Preview tab shows the real thing, and a runtime host mounts the same component with the
 * published revision — which is the whole point of it being a component with one input rather than
 * something assembled inside the preview. A shell built for the preview would drift from the one built for production, and a preview
 * that drifts is worse than none.
 *
 * Everything is bound declaratively. Regions are resolved to {@link RegionView}s and rendered through
 * `NgComponentOutlet`, never created imperatively, so that a definition edited in the neighbouring
 * details form re-renders through ordinary signal propagation instead of a clear-and-rebuild that would
 * drop scroll position and focus on every keystroke.
 *
 * The whole theme is applied here, not only the brand colours: `--pp-*` overrides as a style binding, and
 * a class selecting one of the scoped Material themes of widgets' `src/theme/pp-material-themes.scss`. The
 * class comes from the shell's own {@link ThemeService}: the definition's `materialTheme` / `colorScheme`
 * are its defaults, and a `ThemesButton` placed in one of the regions overrides them for this application
 * only. Both work because a Material theme is a set of custom properties and nothing else. That file is a
 * **global stylesheet the application registers**; if it is not registered the classes still land and the
 * shell simply inherits the host's theme. Overlays opened from inside the shell render into a
 * {@link ShellOverlayContainer} in its host, so they are themed as well.
 *
 * The definition's routes render in the outlet once its host registers them as the children of the route
 * the shell is mounted on — `appShellRoutesGuard` for the Preview, `publishedAppRoutesGuard` for a runtime
 * host.
 *
 * A host may also project controls of its own into the end of the top row, marked {@link ShellActionsDirective}
 * — the platform's controls a definition must not be able to remove, such as the way into the designer.
 */
@Component({
  selector: 'pp-app-shell',
  standalone: true,
  imports: [NgComponentOutlet, MatSidenav, MatSidenavContainer, MatSidenavContent, RouterOutlet, SidenavAutosizeDirective, SidenavContentOutletComponent],
  providers: [
    ThemeService,
    ShellApplicationContext,
    { provide: APPLICATION_CONTEXT, useExisting: ShellApplicationContext },
    { provide: OverlayContainer, useClass: ShellOverlayContainer },
    Dialog,
    MatDialog,
  ],
  // Both theme bindings go on the host rather than on a wrapper, so the element that *is* the shell is
  // also the element they cascade from — and so the host can be the grid, see the stylesheet. `[class]`
  // selects one of the scoped Material themes there; `[style]` carries the `--pp-*` overrides.
  host: { '[class]': 'themeClass()', '[style]': 'themeVars()' },
  template: `
    @if (headerView() || topNavView() || hostActions()) {
      <div class="pp-app-shell__top">
        @if (headerView(); as view) {
          <ng-container *ngComponentOutlet="view.component; inputs: view.inputs"></ng-container>
        }
        @if (topNavView(); as view) {
          <ng-container *ngComponentOutlet="view.component; inputs: topNavInputs()"></ng-container>
        }
        <ng-content select="[ppShellActions]" />
      </div>
    }

    <mat-sidenav-container class="pp-app-shell__body">
      @if (sidenavView(); as view) {
        <mat-sidenav
          class="pp-app-shell__sidenav"
          ppSidenavAutosize
          [mode]="layout().sidenavMode"
          [opened]="layout().sidenavOpened && !layoutService.isSidenavHidden()"
          [position]="layout().sidenavPosition"
        >
          <!-- A page such as a content book may take the sidenav over; the outlet keeps the nav one click away. -->
          <pp-sidenav-content-outlet>
            <ng-container *ngComponentOutlet="view.component; inputs: sidenavInputs()"></ng-container>
          </pp-sidenav-content-outlet>
        </mat-sidenav>
      }
      <mat-sidenav-content>
        <main class="pp-app-shell__content" [style.max-width]="layout().contentMaxWidth">
          <router-outlet />
        </main>
      </mat-sidenav-content>
    </mat-sidenav-container>

    @if (footerView(); as view) {
      <ng-container *ngComponentOutlet="view.component; inputs: view.inputs"></ng-container>
    }
  `,
  styleUrl: 'app-shell.component.scss',
})
export class AppShellComponent {
  /**
   * The definition to render. Optional rather than required because the shell is mounted before the
   * definition resolves — the preview's store selects the entity in its own `ngOnInit`, and a deep link
   * arrives with nothing loaded — so an empty shell is a state to render, not an error.
   */
  readonly definition = input<AppDefinition | undefined>(undefined);

  /**
   * Whether a theme the user picks in a `ThemesButton` inside the shell is remembered, per application id.
   * The designer's preview turns it off: there a choice is only tried out, and a stored one would shadow the
   * theme being edited in the neighbouring form.
   */
  readonly persistTheme = input(true);

  private readonly regionRenderer = inject(AppRegionRenderer);
  private readonly theme = inject(ThemeService);
  private readonly applicationContext = inject(ShellApplicationContext);
  protected readonly layoutService = inject(LayoutService);

  /** Controls the host projected into the top row; the row is rendered for them alone if need be. */
  protected readonly hostActions = contentChild(ShellActionsDirective);

  protected readonly layout = computed(() => layoutOf(this.definition()));
  protected readonly themeVars = computed(() => themeVarsOf(this.definition()));
  protected readonly themeClass = this.theme.themeClass;

  // Re-run on every edit of the definition. Persisting, that re-reads the same stored choice; in the preview
  // it drops the choice tried out there, so an edit to the theme fields always shows.
  private readonly applyThemeDefaults = effect(() => {
    const definition = this.definition();
    this.theme.setDefaults(themeDefaultsOf(definition));
    this.theme.persistUnder(this.persistTheme() && definition?.id ? `pp-theme:${definition.id}` : null);
    this.applicationContext.definition.set(definition);
  });

  private readonly regionViews = computed(() =>
    (this.definition()?.regions ?? []).map((region) => this.regionRenderer.render(region, this.definition())).filter((view): view is RegionView => view !== undefined),
  );

  protected readonly headerView = computed(() => this.viewOf('header'));
  protected readonly footerView = computed(() => this.viewOf('footer'));

  /**
   * The nav in the sidenav, for the two sidenav presets. Split from {@link topNavView} rather than
   * decided in the template, so each slot is one unambiguous `@if` and the mutual exclusion is stated
   * once, here.
   */
  protected readonly sidenavView = computed(() => (this.layout().hasSidenav && !this.isSmallScreen() ? this.viewOf('sidenav') : undefined));

  /**
   * The same nav relocated into the header row under `top-nav`. It is placed beside the header region
   * rather than inside it, so that a `top-nav` app which declares no header region is still navigable —
   * and so that the shell still never invents a region that was not authored.
   */
  protected readonly topNavView = computed(() => (this.layout().hasSidenav || this.isSmallScreen() ? undefined : this.viewOf('sidenav')));

  /**
   * A handset layout has room for neither a sidenav nor a horizontal nav row, so the shell renders the nav
   * in neither place there; a `nav-menu` widget in the header stands in for it, reading the same entries
   * through the APPLICATION_CONTEXT. A definition with no such widget is not navigable on a phone — which is
   * why the seeded applications all place one.
   */
  private readonly isSmallScreen = computed(() => this.layoutService.isSmallDevice());

  /**
   * The nav view's own inputs with the axis the layout dictates laid over them.
   *
   * Computeds rather than a method called from the template: a method would hand `NgComponentOutlet` a
   * freshly built object on every change-detection pass, so it would re-diff and re-apply the nav's
   * inputs continuously. Memoized, the identity only changes when the definition or the preset does.
   */
  // Compact on a tablet layout, the sidenav's middle size: the drawer is sized to its content, so compact
  // rows are what narrow it to a rail. See SidenavAutosizeDirective for how the content margin follows.
  protected readonly sidenavInputs = computed(() => ({ ...this.navInputsOf(this.sidenavView(), 'vertical'), compact: this.layoutService.isMediumDevice() }));
  protected readonly topNavInputs = computed(() => this.navInputsOf(this.topNavView(), 'horizontal'));

  private navInputsOf(view: RegionView | undefined, orientation: NavOrientation): Record<string, unknown> {
    return { ...view?.inputs, orientation };
  }

  private viewOf(slot: RegionView['slot']): RegionView | undefined {
    // First occurrence wins. An app declares each slot at most once — `type` is the region's identity,
    // see APP_REGION_ID_FIELD — so a second one is a definition that bypassed validation, and ignoring
    // it is the harmless read. The same rule `buildAppRoutes` applies to a duplicate path.
    return this.regionViews().find((view) => view.slot === slot);
  }
}
